// src/pages/ViewStatus.jsx
// src/pages/ViewStatus.jsx

import React, {
  useEffect,
  useState,
  useRef,
} from "react";

import { useSearchParams } from "react-router-dom";
import { io } from "socket.io-client";

import "./ViewStatus.css";
import Navbar from "./Navbar";
import {
  STATUS_REACTIONS,
  reactToStatus,
  refId,
} from "../services/statusApi";


const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

// Socket created once (module scope) so it persists across re-renders,
// mirroring the pattern already used in Notifications.jsx / Navbar.jsx.
const socket = io(BASE_URL, {
  transports: ["websocket", "polling"],
});


function ViewStatus() {
  const [searchParams] = useSearchParams();
  const [statuses, setStatuses] =
    useState([]);

  const [openStory, setOpenStory] =
    useState(false);

  const [
    currentUserIndex,
    setCurrentUserIndex,
  ] = useState(0);

  const [
    currentStoryIndex,
    setCurrentStoryIndex,
  ] = useState(0);

  const [storyProgress, setStoryProgress] =
    useState(0);

  // Loading lets us distinguish "still fetching" from "no status exists",
  // which matters for the deep-link empty state below.
  const [loading, setLoading] =
    useState(true);

  // Set when we deep-link to a user (via ?user / ?username) who turns out
  // to have no active status — drives the empty-state message.
  const [targetNotFound, setTargetNotFound] =
    useState(false);

  const videoRef = useRef(null);

  // Ensures we only auto-open the deep-linked story once per navigation.
  const deepLinkHandled = useRef(false);

  const userId =
    localStorage.getItem("userId");

  // =========================
  // FETCH STATUS
  // =========================

  const getStatuses = async () => {
    try {
      // When a specific user is deep-linked (profile / blog avatar click) we
      // fetch ONLY that user's statuses, filtered by id on the server — never
      // the global list. This guarantees the viewer can only ever show the
      // clicked user's stories. Without a target we keep the global feed
      // (the "Recent updates" list reached from the navbar).
      const targetUserId =
        searchParams.get("user");

      const endpoint = targetUserId
        ? `${BASE_URL}/api/status/my/${targetUserId}`
        : `${BASE_URL}/api/status/all`;

      const res = await fetch(endpoint);

      const data =
        await res.json();

      setStatuses(
        Array.isArray(data) ? data : []
      );
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // MARK VIEW
  // =========================

  const markAsViewed = async (
    statusId
  ) => {
    try {
      await fetch(
        `${BASE_URL}/api/status/view/${statusId}`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify({
            userId,
          }),
        }
      );
    } catch (err) {
      console.log(err);
    }
  };

  // =========================
  // GROUP
  // =========================

  const groupedStatuses =
    Object.values(
      statuses.reduce(
        (acc, status) => {
          const uid =
            status.user?._id;

          if (!uid) return acc;

          if (!acc[uid]) {
            acc[uid] = {
              user: status.user,
              statuses: [],
            };
          }

          acc[uid].statuses.push(
            status
          );

          return acc;
        },
        {}
      )
    );

  // =========================
  // OPEN STORY
  // =========================

  const openStoryViewer = (
    userIndex
  ) => {
    setCurrentUserIndex(
      userIndex
    );

    setCurrentStoryIndex(0);

    setStoryProgress(0);

    setOpenStory(true);
  };

  // =========================
  // NEXT
  // =========================

  const nextStory = () => {
    const currentUser =
      groupedStatuses[
        currentUserIndex
      ];

    if (!currentUser) {
      setOpenStory(false);
      return;
    }

    if (
      currentStoryIndex <
      currentUser.statuses.length -
        1
    ) {
      setCurrentStoryIndex(
        (prev) => prev + 1
      );

      setStoryProgress(0);
    } else if (
      currentUserIndex <
      groupedStatuses.length - 1
    ) {
      setCurrentUserIndex(
        (prev) => prev + 1
      );

      setCurrentStoryIndex(0);

      setStoryProgress(0);
    } else {
      setOpenStory(false);
    }
  };

  // =========================
  // PREV
  // =========================

  const prevStory = () => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex(
        (prev) => prev - 1
      );

      setStoryProgress(0);
    } else if (
      currentUserIndex > 0
    ) {
      const prevUser =
        groupedStatuses[
          currentUserIndex - 1
        ];

      setCurrentUserIndex(
        (prev) => prev - 1
      );

      setCurrentStoryIndex(
        prevUser.statuses.length -
          1
      );

      setStoryProgress(0);
    }
  };

  // =========================
  // IMAGE AUTO
  // =========================

  useEffect(() => {
    if (!openStory) return;

    const current =
      groupedStatuses[
        currentUserIndex
      ]?.statuses[
        currentStoryIndex
      ];

    if (!current) return;

    if (
      current.mediaType ===
      "image"
    ) {
      const duration = 5000;

      const startTime =
        Date.now();

      const interval =
        setInterval(() => {
          const elapsed =
            Date.now() -
            startTime;

          const progress =
            Math.min(
              (elapsed /
                duration) *
                100,
              100
            );

          setStoryProgress(
            progress
          );

          if (
            progress >= 100
          ) {
            clearInterval(
              interval
            );

            nextStory();
          }
        }, 50);

      return () =>
        clearInterval(
          interval
        );
    }
  }, [
    openStory,
    currentStoryIndex,
    currentUserIndex,
  ]);

  // =========================
  // REACT TO STATUS
  // Optimistic toggle/replace, then reconcile with the server response.
  // =========================

  const handleReact = async (
    statusId,
    emoji
  ) => {
    if (!userId) return;

    setStatuses((prev) =>
      prev.map((s) => {
        if (s._id !== statusId) return s;

        const others = (
          s.reactions || []
        ).filter(
          (r) => refId(r.user) !== userId
        );

        const mine = (
          s.reactions || []
        ).find(
          (r) => refId(r.user) === userId
        );

        const next =
          mine && mine.emoji === emoji
            ? others
            : [
                ...others,
                {
                  user: { _id: userId },
                  emoji,
                },
              ];

        return { ...s, reactions: next };
      })
    );

    try {
      const data = await reactToStatus(
        statusId,
        userId,
        emoji
      );

      if (data?.reactions) {
        setStatuses((prev) =>
          prev.map((s) =>
            s._id === statusId
              ? {
                  ...s,
                  reactions: data.reactions,
                }
              : s
          )
        );
      }
    } catch (err) {
      console.log(err);
      // Reconcile with server truth if the request failed.
      getStatuses();
    }
  };

  // =========================
  // LOAD
  // =========================

  useEffect(() => {
    getStatuses();
  }, []);

  // =========================
  // LIVE REACTION UPDATES (socket)
  // =========================

  useEffect(() => {
    if (userId) socket.emit("join", userId);

    const onReaction = ({
      statusId,
      reactions,
    }) => {
      setStatuses((prev) =>
        prev.map((s) =>
          s._id === statusId
            ? { ...s, reactions }
            : s
        )
      );
    };

    socket.on("statusReaction", onReaction);

    return () => {
      socket.off(
        "statusReaction",
        onReaction
      );
    };
  }, [userId]);

  // =========================
  // DEEP LINK (?user=<id> / ?username=<name>)
  // Auto-open a specific user's story when navigated from a profile / blog.
  // =========================

  useEffect(() => {
    if (loading || deepLinkHandled.current)
      return;

    const targetId =
      searchParams.get("user");
    const targetName =
      searchParams.get("username");

    if (!targetId && !targetName) return;

    deepLinkHandled.current = true;

    const idx = groupedStatuses.findIndex(
      (g) =>
        (targetId &&
          g.user?._id === targetId) ||
        (targetName &&
          g.user?.name
            ?.trim()
            .toLowerCase() ===
            targetName
              .trim()
              .toLowerCase())
    );

    if (idx >= 0) {
      openStoryViewer(idx);
      markAsViewed(
        groupedStatuses[idx].statuses[0]._id
      );
    } else {
      setTargetNotFound(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, searchParams]);

  return (
    <div className="whatsapp-status-page">
      <Navbar />

      <div className="bg-circle bg1"></div>
      <div className="bg-circle bg2"></div>
      <div className="bg-circle bg3"></div>

      <div className="status-wrapper">
        <div className="status-header">
          <h2>Status</h2>

          <p>
            Recent updates
          </p>

    
        </div>

        <div className="status-list">
          {loading && (
            <p className="status-empty-sub">
              Loading statuses…
            </p>
          )}

          {!loading &&
            groupedStatuses.length === 0 && (
              <p className="status-empty-sub">
                No statuses yet. Be the first
                to share one!
              </p>
            )}

          {groupedStatuses.map(
            (
              group,
              userIndex
            ) => (
              <div
                key={
                  group.user
                    ?._id
                }
                className="status-item"
                onClick={() => {
                  openStoryViewer(
                    userIndex
                  );

                  markAsViewed(
                    group
                      .statuses[0]
                      ._id
                  );
                }}
              >
                <div className="status-avatar-ring">
                  <img
                    src={
                      group.user
                        ?.profilePic
                    }
                    alt=""
                    className="status-avatar"
                  />
                </div>

                <div className="status-user-info">
                  <h4>
                    {
                      group.user
                        ?.name
                    }
                  </h4>

                  <p>
                    {
                      group
                        .statuses
                        .length
                    }{" "}
                    updates
                  </p>
                </div>
              </div>
            )
          )}
        </div>
      </div>

      {openStory &&
        groupedStatuses[
          currentUserIndex
        ] && (
          <div className="story-overlay">
            <div className="story-top">
              <div className="multi-progress">
                {groupedStatuses[
                  currentUserIndex
                ].statuses.map(
                  (_, i) => (
                    <div
                      className="progress-line"
                      key={i}
                    >
                      <div
                        className="progress-fill"
                        style={{
                          width:
                            i <
                            currentStoryIndex
                              ? "100%"
                              : i ===
                                currentStoryIndex
                              ? `${storyProgress}%`
                              : "0%",
                        }}
                      />
                    </div>
                  )
                )}
              </div>

              <div className="story-user-row">
                <img
                  src={
                    groupedStatuses[
                      currentUserIndex
                    ].user
                      ?.profilePic
                  }
                  alt=""
                  className="story-user-img"
                />

                <div>
                  <h4>
                    {
                      groupedStatuses[
                        currentUserIndex
                      ].user?.name
                    }
                  </h4>
                </div>

                <button
                  className="close-story"
                  onClick={() =>
                    setOpenStory(
                      false
                    )
                  }
                >
                  ✕
                </button>
              </div>
            </div>

            <div
              className="story-left"
              onClick={
                prevStory
              }
            ></div>

            <div className="story-content">
              {groupedStatuses[
                currentUserIndex
              ].statuses[
                currentStoryIndex
              ].mediaType ===
              "image" ? (
                <img
                  src={
                    groupedStatuses[
                      currentUserIndex
                    ].statuses[
                      currentStoryIndex
                    ].media
                  }
                  alt=""
                  className="story-media"
                />
              ) : (
                <video
                  ref={
                    videoRef
                  }
                  src={
                    groupedStatuses[
                      currentUserIndex
                    ].statuses[
                      currentStoryIndex
                    ].media
                  }
                  autoPlay
                  playsInline
                  muted
                  className="story-media"
                  onTimeUpdate={(
                    e
                  ) => {
                    const video =
                      e.target;

                    if (
                      video.duration
                    ) {
                      setStoryProgress(
                        (
                          video.currentTime /
                          video.duration
                        ) *
                          100
                      );
                    }
                  }}
                  onEnded={
                    nextStory
                  }
                />
              )}
            </div>

            <div
              className="story-right"
              onClick={
                nextStory
              }
            ></div>

            {/* ===== REACTIONS BAR ===== */}
            {(() => {
              const cur =
                groupedStatuses[
                  currentUserIndex
                ].statuses[
                  currentStoryIndex
                ];

              const reactions =
                cur.reactions || [];

              const myReaction = reactions.find(
                (r) =>
                  refId(r.user) === userId
              )?.emoji;

              const counts = reactions.reduce(
                (acc, r) => {
                  acc[r.emoji] =
                    (acc[r.emoji] || 0) + 1;
                  return acc;
                },
                {}
              );

              return (
                <div
                  className="story-reactions"
                  onClick={(e) =>
                    e.stopPropagation()
                  }
                >
                  {STATUS_REACTIONS.map(
                    (em) => (
                      <button
                        key={em}
                        type="button"
                        className={`story-reaction-btn${
                          myReaction === em
                            ? " active"
                            : ""
                        }`}
                        onClick={() =>
                          handleReact(
                            cur._id,
                            em
                          )
                        }
                        aria-label={`React ${em}`}
                      >
                        <span className="sr-emoji">
                          {em}
                        </span>
                        {counts[em] ? (
                          <span className="sr-count">
                            {counts[em]}
                          </span>
                        ) : null}
                      </button>
                    )
                  )}
                </div>
              );
            })()}
          </div>
        )}

      {/* ===== DEEP-LINK EMPTY STATE ===== */}
      {targetNotFound && !openStory && (
        <div className="status-empty-banner">
          <div className="status-empty-icon">
            🕊️
          </div>
          <p className="status-empty-title">
            No active status
           
          </p>
          <p className="status-empty-sub">
            This user hasn't shared a status, or
            it has expired.
          </p>
        </div>
      )}
    </div>
  );
}

export default ViewStatus;